// Buttons pressed while a text field has the on-screen keyboard up.
//
// iOS does not stop at a canceled pointerdown: it still sends mousedown, and
// that mousedown takes the focus from the field. The keyboard goes down, what
// sat above it moves down with the visible part of the screen, and the click
// lands on whatever is now under the finger, not on the button (2026-10-18, an
// iPhone: the post box's pills did nothing with the keyboard up). Old fixes on
// the same screens: the send button (2026-10-01) and the conditions drawer
// (2026-09-28).
//
// Promise:
// - `KEEP_FOCUS` on a button: pressing it leaves the focus (and the keyboard)
//   where it is; the click reaches the button.
// - `openOnPress(open)` on a button that opens something over the screen:
//   a finger or a pen opens it at the press, not at the click (the keyboard
//   going down and the drawer coming up overlap, and a finger that moves a
//   little still opens it); the click that follows the release is swallowed
//   once, so it does not land on what just opened (its scrim closes it). A
//   mouse and the keyboard open it by the click. `open` hears whether a
//   finger, a pen or the mouse pressed it (not the keyboard).
//   It also opens when iOS sends no click at all: iOS drops the click of a
//   tap during which new things to press were drawn anywhere on the page
//   (it takes the tap for a hover that showed a menu), while the press
//   itself still comes. The Dock's "⋯" did nothing at the first tap after
//   the home screen app opened: the Dock and the list's "+" were being drawn
//   (the iOS 26.5 simulator, 2026-10-19).
import type { MouseEvent, PointerEvent } from "react";

/** How long after the release the click of that press may still come. */
const CLICK_AFTER_RELEASE_MS = 400;
/** A press held longer than this is not followed by a click anyway. */
const HOLD_LIMIT_MS = 10_000;

const keep = (e: { preventDefault(): void }) => e.preventDefault();

/** Pressing does not take the focus from the field being written. */
export const KEEP_FOCUS = {
  onPointerDown: keep,
  onMouseDown: keep,
} as const;

/** Swallows the click that follows the release of `pointerId`, once. */
function swallowClickOf(pointerId: number): void {
  let timer = 0;
  const off = () => {
    window.clearTimeout(timer);
    document.removeEventListener("click", swallow, true);
    document.removeEventListener("pointerup", released, true);
    document.removeEventListener("pointercancel", released, true);
  };
  const swallow = (e: Event) => {
    e.preventDefault();
    // Also from what listens on the document after it (what just opened
    // closes at a click outside it: the Dock's menu).
    e.stopImmediatePropagation();
    off();
  };
  const released = (e: Event) => {
    if ((e as globalThis.PointerEvent).pointerId !== pointerId) return;
    window.clearTimeout(timer);
    timer = window.setTimeout(off, CLICK_AFTER_RELEASE_MS);
  };
  document.addEventListener("click", swallow, true);
  document.addEventListener("pointerup", released, true);
  document.addEventListener("pointercancel", released, true);
  timer = window.setTimeout(off, HOLD_LIMIT_MS);
}

/** A button that opens something over the screen; see the promise above. */
export function openOnPress(
  open: (button: HTMLButtonElement, pointer: boolean) => void,
) {
  return {
    onPointerDown(e: PointerEvent<HTMLButtonElement>) {
      e.preventDefault();
      if (e.pointerType === "mouse" || e.button !== 0) return;
      open(e.currentTarget, true);
      swallowClickOf(e.pointerId);
    },
    onMouseDown: keep,
    onClick(e: MouseEvent<HTMLButtonElement>) {
      open(e.currentTarget, e.detail > 0);
    },
  };
}
